import { ArrowUpFromSquare } from "@gravity-ui/icons";
import { Button, Flex, Icon, Text } from "@gravity-ui/uikit";
import { useCallback, useRef, useState } from "react";
import { useTranslate } from "src/i18n";

import {
  WATERMARK_ACCEPT_MIME,
  WATERMARK_MAX_PER_USER,
} from "../WatermarkOptionsForm.constants";
import {
  validateWatermarkFile,
  ValidationReason,
} from "../WatermarkOptionsForm.utils";
import css from "./WatermarkUploader.module.scss";

interface WatermarkUploaderProps {
  disabled?: boolean;
  currentCount?: number;
  onUpload: (file: File) => void;
}

export const WatermarkUploader = ({
  disabled,
  currentCount = 0,
  onUpload,
}: WatermarkUploaderProps) => {
  const { t } = useTranslate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const limitReached = currentCount >= WATERMARK_MAX_PER_USER;

  const validateFile = useCallback(
    (file: File): string | null => {
      const result = validateWatermarkFile(file);
      if (!result.valid) {
        if (result.reason === ValidationReason.Mime) {
          return t(
            "watermark.error.mimeType",
            "Допустимы только файлы PNG, JPEG или WebP",
          );
        }
        if (result.reason === ValidationReason.Size) {
          return t("watermark.error.tooLarge", "Файл больше 5 МБ");
        }
      }
      return null;
    },
    [t],
  );

  const handleFile = useCallback(
    (file: File) => {
      setError(null);
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      onUpload(file);
    },
    [onUpload, validateFile],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (disabled || limitReached) return;
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [disabled, limitReached, handleFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled && !limitReached) setIsDragOver(true);
    },
    [disabled, limitReached],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      if (inputRef.current) inputRef.current.value = "";
    },
    [handleFile],
  );

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const uploaderClasses = [css.uploader];
  if (isDragOver) uploaderClasses.push(css.dragover);
  if (disabled || limitReached) uploaderClasses.push(css.disabled);

  return (
    <Flex direction="column" gap={1}>
      <div
        className={uploaderClasses.join(" ")}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        role="button"
        aria-label={t(
          "watermark.uploaderAriaLabel",
          "Зона загрузки watermark-картинки",
        )}
        tabIndex={disabled || limitReached ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
        onClick={handleClick}
      >
        <input
          ref={inputRef}
          type="file"
          accept={WATERMARK_ACCEPT_MIME}
          className={css.hiddenInput}
          onChange={handleInputChange}
          disabled={disabled || limitReached}
        />
        <Flex direction="column" alignItems="center" gap={2}>
          <Icon data={ArrowUpFromSquare} size={24} />
          <Text variant="body-1">
            {limitReached
              ? t(
                  "watermark.limitReached",
                  "Достигнут лимит {{count}}/{{max}}. Удалите неиспользуемые.",
                  {
                    count: String(currentCount),
                    max: String(WATERMARK_MAX_PER_USER),
                  },
                )
              : t(
                  "watermark.uploaderHint",
                  "Перетащите файл сюда или нажмите для выбора",
                )}
          </Text>
          {!limitReached && (
            <Button
              view="action"
              size="m"
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
            >
              {t("watermark.selectFile", "Выбрать файл")}
            </Button>
          )}
          <Text variant="caption-2" color="secondary">
            {t("watermark.uploaderFormats", "PNG, JPEG, WebP · до 5 МБ")}
          </Text>
        </Flex>
      </div>
      {error && (
        <Text variant="caption-2" color="danger">
          {error}
        </Text>
      )}
    </Flex>
  );
};
